import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Progress,
  Box,
  Checkbox,
} from "@chakra-ui/react";
import { BASE_URLS, tableName, getWTFFormsConfig } from "./env";
import { getSupabase } from "../components/supabaseClient";
import {
  extractAllSubmissionLinks,
  addFormToSubmissionsArray,
} from "../utils/submissionUtils";

const BulkFormGenerationModal = ({
  isOpen,
  onClose,
  date,
  onRefresh,
  selectedLLMModel = 0,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPatient, setCurrentPatient] = useState("");
  const [results, setResults] = useState([]);
  const toast = useToast();

  const handleBulkGenerate = async () => {
    if (!date) {
      toast({
        title: "Error",
        description: "No date provided for bulk generation",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setResults([]);

    try {
      // Get backend URL (matches FormSelectionModal logic)
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // Simulate progress
      setProgress(20);
      setCurrentPatient("Initializing bulk generation...");

      const response = await fetch(`${baseUrl}/api/bulk-generate-forms/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          date: date,
          generate_for_all: false,
          model: selectedLLMModel, // Use selected LLM model
        }),
      });

      setProgress(60);
      setCurrentPatient("Processing forms...");

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate forms");
      }

      const result = await response.json();
      setProgress(90);
      setCurrentPatient("Finalizing...");

      if (result.success) {
        setProgress(100);
        setResults(result.results || []);

        const { summary } = result;

        // After successful bulk generation, add forms to submissions for FFP/FFS patients
        console.log(
          "[BULK SUBMISSION] Processing bulk form generation result:",
          result
        );
        await processBulkFormSubmissions(result);

        // Determine toast status based on results
        let toastStatus = "success";
        let toastDuration = 5000;

        if (summary?.errors > 0) {
          toastStatus = "error";
          toastDuration = 7000;
        } else if (summary?.warnings > 0) {
          toastStatus = "warning";
          toastDuration = 7000;

          // Check if warnings are due to network issues
          if (result.message && result.message.includes("network issues")) {
            toast({
              title: "Bulk Generation Complete - Network Issues Detected",
              description:
                result.message +
                " The forms were generated with generic questions and can be used normally.",
              status: "warning",
              duration: 10000,
              isClosable: true,
            });
            return;
          }
        }

        toast({
          title: "Bulk Generation Complete",
          description:
            result.message ||
            `Generated ${summary?.success || 0} forms successfully`,
          status: toastStatus,
          duration: toastDuration,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Error in bulk generation:", error);

      // Provide more user-friendly error messages
      let errorMessage =
        error.message || "An error occurred during bulk generation";
      if (
        errorMessage.includes("getaddrinfo failed") ||
        errorMessage.includes("Network error")
      ) {
        errorMessage =
          "Network error: Unable to connect to the AI service. Forms will be generated with generic questions. Please try again later.";
      }

      toast({
        title: "Bulk Generation Issue",
        description: errorMessage,
        status: "error",
        duration: 7000, // Show longer for network errors
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
      setCurrentPatient("");
      setProgress(0);
    }
  };

  // Function to process bulk form submissions
  const processBulkFormSubmissions = async (bulkResult) => {
    try {
      const supabase = await getSupabase();

      if (
        !bulkResult.patients_processed ||
        !Array.isArray(bulkResult.patients_processed)
      ) {
        console.log("[BULK SUBMISSION] No patients processed in bulk result");
        return;
      }

      console.log(
        `[BULK SUBMISSION] Processing ${bulkResult.patients_processed.length} patients for submissions`
      );

      let successCount = 0;
      let totalProcessed = 0;

      for (const patient of bulkResult.patients_processed) {
        try {
          const patientType = patient.type_of_patient?.toUpperCase();
          totalProcessed++;

          // Add to submissions for ALL patient types (not just FFP/FFS)
          console.log(
            `[BULK SUBMISSION] Processing patient: ${patient.full_name} (Type: ${patientType})`
          );
          console.log(`[BULK SUBMISSION] Patient data:`, patient);

          // Use the patient_db_id if available, otherwise fetch by name and date
          let patientId = patient.patient_db_id;
          console.log(
            `[BULK SUBMISSION] Initial patientId from patient_db_id:`,
            patientId,
            typeof patientId
          );

          // Ensure patientId is a string for consistent handling
          if (patientId) {
            patientId = String(patientId);
          }

          if (!patientId) {
            console.log(
              `[BULK SUBMISSION] No patient_db_id found, fetching by name and date...`
            );
            // Fallback: Get the patient ID from the database using name and date
            const { data: patientRecord, error: fetchError } = await supabase
              .from(tableName)
              .select("id")
              .eq("date_", bulkResult.date)
              .eq("patient_name", patient.full_name)
              .single();

            console.log(`[BULK SUBMISSION] Fallback query result:`, {
              patientRecord,
              fetchError,
            });

            if (fetchError) {
              console.error(
                `[BULK SUBMISSION] Error fetching patient record for ${patient.full_name}:`,
                fetchError
              );
              continue;
            }
            patientId = patientRecord.id;
            console.log(`[BULK SUBMISSION] Fallback patientId:`, patientId);
          }

          if (
            patientId &&
            (patient.form_link ||
              patient.storage_path ||
              (patient.form_id && patient.submission_id))
          ) {
            console.log(
              `[BULK SUBMISSION] Adding form to submissions - Patient ID: ${patientId}, Form Link: ${patient.form_link}, Storage Path: ${patient.storage_path}, Form ID: ${patient.form_id}, Submission ID: ${patient.submission_id}`
            );

            let formUrl;
            let options = {};

            // Prioritize form_link from backend (matches FormSelectionModal logic)
            if (patient.form_link) {
              // Use the form_link provided by the backend (same as FormSelectionModal)
              formUrl = patient.form_link;
              options = {
                formId: patient.form_id || null,
                submissionId: patient.submission_id || null,
                isWTFForm: !!patient.form_id, // If form_id exists, it's a WTF form
              };
              console.log(
                `[BULK SUBMISSION] Using backend-provided form_link for ${patient.full_name}:`,
                formUrl
              );
            } else if (patient.form_id) {
              // Fallback: Construct WTF_forms URL if form_link is missing
              const config = getWTFFormsConfig();
              formUrl = `${config.baseURL}${config.formsPath}/${patient.form_id}`;
              options = {
                formId: patient.form_id,
                submissionId: patient.submission_id || null,
                isWTFForm: true,
              };
              console.log(
                `[BULK SUBMISSION] Fallback: Generated WTF_forms URL for ${patient.full_name}:`,
                formUrl
              );
            } else if (patient.storage_path) {
              // Legacy Supabase storage path
              const { supabaseUrl, bucketName_files } = await import(
                "../components/env"
              );
              formUrl = `${supabaseUrl}/storage/v1/object/public/${bucketName_files}/${patient.storage_path}`;
              console.log(
                `[BULK SUBMISSION] Using legacy Supabase URL for ${patient.full_name}:`,
                formUrl
              );
            }

            console.log(
              `[BULK SUBMISSION] About to call addFormToSubmissionsArray with:`,
              {
                patientId,
                formUrl,
                patientName: patient.full_name,
                options,
              }
            );

            // Use addFormToSubmissionsArray from submissionUtils.js to add the form to the patient's submissions
            const success = await addFormToSubmissionsArray(
              patientId,
              formUrl,
              options
            );
            if (success) {
              successCount++;
              console.log(
                `[BULK SUBMISSION] Successfully added form to submissions for ${patient.full_name} (${patientType})`
              );

              // Verify the submission was added by checking the submission links
              try {
                const submissionLinks = await extractAllSubmissionLinks(
                  patientId
                );
                console.log(
                  `[BULK SUBMISSION] Verification - Patient ${patient.full_name} has ${submissionLinks.length} submission links:`,
                  submissionLinks
                );
              } catch (verifyError) {
                console.error(
                  `[BULK SUBMISSION] Error verifying submissions for ${patient.full_name}:`,
                  verifyError
                );
              }
            } else {
              console.error(
                `[BULK SUBMISSION] Failed to add form to submissions for ${patient.full_name} (${patientType})`
              );
            }
          } else {
            console.warn(
              `[BULK SUBMISSION] Missing patient ID or form information for ${patient.full_name}. Patient ID: ${patientId}, Storage Path: ${patient.storage_path}, Form ID: ${patient.form_id}, Submission ID: ${patient.submission_id}`
            );
          }
        } catch (error) {
          console.error(
            `[BULK SUBMISSION] Error processing patient ${patient.full_name}:`,
            error
          );
        }
      }

      console.log(
        `[BULK SUBMISSION] Summary: Added ${successCount}/${totalProcessed} forms to submissions for all patients`
      );

      if (successCount > 0) {
        toast({
          title: "Forms Added to Submissions",
          description: `Successfully added ${successCount} forms to the submission column for all patient types.`,
          status: "info",
          duration: 5000,
          isClosable: true,
        });

        // Call the refresh callback to update the parent component's data
        if (onRefresh) {
          console.log("[BULK SUBMISSION] Triggering refresh of parent data...");
          onRefresh();
        }
      }
    } catch (error) {
      console.error(
        "[BULK SUBMISSION] Error processing bulk form submissions:",
        error
      );
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      setResults([]);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!isGenerating}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Bulk Form Generation</ModalHeader>
        <ModalCloseButton isDisabled={isGenerating} />
        <ModalBody>
          <VStack spacing={4} align="start">
            <Text>
              Generate forms for date: <strong>{date}</strong>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Default: Only generate forms for patients without existing forms
              </Text>
            </Text>

            {isGenerating && (
              <VStack spacing={2} width="100%">
                <Text>Generating forms...</Text>
                <Progress value={progress} width="100%" />
                <Text fontSize="sm" color="gray.600">
                  Current patient: {currentPatient}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Progress: {progress}%
                </Text>
              </VStack>
            )}

            {results.length > 0 && !isGenerating && (
              <VStack spacing={2} width="100%" align="start">
                <Text fontWeight="bold">Generation Results:</Text>
                <Box maxHeight="200px" overflowY="auto" width="100%">
                  {results.map((result, index) => (
                    <Text
                      key={index}
                      fontSize="sm"
                      color={
                        result.status === "success"
                          ? "green.600"
                          : result.status === "warning"
                          ? "yellow.600"
                          : "red.600"
                      }
                    >
                      {result.patient_name}:{" "}
                      {result.status === "success"
                        ? "Success"
                        : result.status === "warning"
                        ? `Warning: ${result.message}`
                        : `Error: ${result.error}`}
                    </Text>
                  ))}
                </Box>
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isGenerating}
          >
            {results.length > 0 ? "Close" : "Cancel"}
          </Button>
          {results.length === 0 && (
            <Button
              colorScheme="purple"
              onClick={handleBulkGenerate}
              isLoading={isGenerating}
            >
              Generate Forms
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkFormGenerationModal;
